import CustomTooltip from '../../shared/CustomTooltip';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    Box,
    Divider,
    FormControl,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Menu,
    MenuItem,
    Select,
    type SelectChangeEvent,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha, lighten } from '@mui/material/styles';
import { type MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useGetUserListQuery } from '../../../app/Slices/SevenDayApiSlice';
import { selectFilteredUserData } from '../../../app/Slices/UserFilterSlice';
import { CustomChip } from '../../../layout/styledcomponents/CustomChip';
import UserRole from '../../../shared/AuthRoles';
import { capitalizeName } from '../../../shared/utils';
import GenericTable, { LinkType } from '../../shared/GenericTable';
import ProfilePicture from '../Profile/ProfilePicture';
import { ContractorRole } from '../../enums/users';
import { buildUsersListUrl } from './userListQueryUrl';
import { matchesUserListStatusFilter } from './usersStatsFilter';

function userProfilePath(row: { firstName?: string; id?: number }) {
    return `/users/${(row.firstName || 'user').replace(/ /g, '').toLowerCase()}/${row.id}`;
}

function UserRowActionsMenu({ row }: { row: Record<string, unknown> }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const to = userProfilePath(row as { firstName?: string; id?: number });

    return (
        <>
            <IconButton
                type='button'
                size='small'
                aria-label='User actions'
                aria-haspopup='menu'
                aria-expanded={open ? 'true' : undefined}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{ color: 'text.secondary' }}
            >
                <MoreVertIcon fontSize='small' />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem
                    component={Link}
                    to={to}
                    state={{ name: 'viewUser', params: row }}
                    onClick={() => setAnchorEl(null)}
                    sx={{ typography: 'body2' }}
                >
                    View user
                </MenuItem>
            </Menu>
        </>
    );
}

function roleAccessLabel(
    row: { role?: string; viewOnly?: boolean },
    viewer: { role?: string } | undefined,
): string {
    if (
        row?.role === 'owner_admin' ||
        row?.role === ContractorRole.Admin ||
        viewer?.role === 'admin'
    ) {
        return 'ADMIN';
    }
    if (row?.role === 'guest_admin') return 'COMPANY ADMIN';
    if (row?.role === 'guest_department_admin') return 'TEAM ADMIN';
    if (row?.viewOnly === true) return 'USER (VIEW ONLY)';
    return 'USER';
}

function roleAccessLabelExport(
    row: { role?: string; viewOnly?: boolean },
    viewer: { role?: string } | undefined,
): string {
    if (
        row?.role === 'owner_admin' ||
        row?.role === ContractorRole.Admin ||
        viewer?.role === 'admin'
    ) {
        return 'Admin';
    }
    if (row?.role === 'guest_admin') return 'Company Admin';
    if (row?.role === 'guest_department_admin') return 'Team Admin';
    if (row?.viewOnly === true) return 'User (View only)';
    return 'User';
}

function matchesRoleStatusFilter(val: any, filterResults: any): boolean {
    if (
        filterResults?.role === undefined &&
        filterResults?.status === undefined
    ) {
        return true;
    }
    if (
        filterResults?.role !== undefined &&
        filterResults?.status !== undefined
    ) {
        if (filterResults?.role === 'All' && filterResults?.status === 'All') {
            return true;
        }
        if (filterResults?.role !== 'All' && filterResults?.status !== 'All') {
            return (
                val?.role
                    ?.toLowerCase()
                    ?.includes(filterResults?.role?.toLowerCase()) &&
                matchesUserListStatusFilter(val, filterResults?.status)
            );
        }
        if (filterResults?.role === 'All' && filterResults?.status !== 'All') {
            return matchesUserListStatusFilter(val, filterResults?.status);
        }
        if (filterResults?.role !== 'All' && filterResults?.status === 'All') {
            return val?.role
                ?.toLowerCase()
                ?.includes(filterResults?.role?.toLowerCase());
        }
        return true;
    }
    return true;
}

export default function UsersComponent() {
    const navigate = useNavigate();
    const loggedInUser = UserRole();
    const filterResults: any = useSelector(selectFilteredUserData);
    const listUrl = useMemo(
        () => buildUsersListUrl(loggedInUser),
        [loggedInUser],
    );
    const skipList = !listUrl;
    const { data: getUsers, isLoading: isUserListLoading } =
        useGetUserListQuery(listUrl, { skip: skipList });
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const theme = useTheme();

    const [mobileSort, setMobileSort] = useState<'lastActive' | 'name'>(
        'lastActive',
    );

    const roleAccessBadgeSx = (highlighted: boolean) => ({
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        width: 'fit-content',
        maxWidth: '100%',
        px: 1.25,
        py: 0.375,
        borderRadius: 3,
        fontSize: '10px',
        lineHeight: 1.2,
        fontWeight: 600,
        letterSpacing: 0.01,
        ...(highlighted
            ? {
                  color: theme.palette.blue.main,
                  bgcolor: lighten(theme.palette.blue.main, 0.78),
              }
            : {
                  color: 'text.primary',
                  bgcolor: alpha(theme.palette.text.primary, 0.08),
              }),
    });

    const loggedInUserBadge = () => ({
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        width: 'fit-content',
        maxWidth: '100%',
        px: 1.25,
        py: 0.375,
        borderRadius: 3,
        typography: 'body2',
        fontWeight: 600,
        letterSpacing: 0.01,
        fontSize: '10px',
        color: theme.palette.yellow.main,
        bgcolor: lighten(theme.palette.yellow.main, 0.78),
    });

    const users = useMemo(() => {
        if (!getUsers) return undefined;
        const searchQ = (filterResults?.search ?? '').trim().toLowerCase();
        const teamFilter = filterResults?.team ?? 'All';

        return getUsers.filter((val: any) => {
            if (searchQ) {
                const deptName = val?.department?.name ?? '';
                const roleLabel = roleAccessLabel(val, loggedInUser);
                const accessKind =
                    val?.viewOnly === true ? 'view access' : 'full access';
                const statusTerms =
                    val?.isActive === true
                        ? ['enabled', 'active']
                        : val?.isActive === false
                          ? ['disabled', 'inactive']
                          : [];
                const blob = [
                    val.firstName,
                    val.lastName,
                    val.email,
                    val.role,
                    roleLabel,
                    accessKind,
                    deptName,
                    deptName ? capitalizeName(deptName) : '',
                    ...statusTerms,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                if (!blob.includes(searchQ)) return false;
            }
            if (teamFilter !== 'All') {
                if (val?.department?.name !== teamFilter) return false;
            }
            return matchesRoleStatusFilter(val, filterResults);
        });
    }, [getUsers, filterResults, loggedInUser]);

    const processedData = useMemo(() => {
        if (!users) return [];
        return users.map((row: any) => ({
            ...row,
            lastLoginAt: row.lastLoginAt
                ? new Date(new Date(row.lastLoginAt))
                : null,
        }));
    }, [users]);

    const sortedMobileRows = useMemo(() => {
        const arr = [...processedData];
        if (mobileSort === 'name') {
            arr.sort((a, b) =>
                `${a.firstName ?? ''} ${a.lastName ?? ''}`.localeCompare(
                    `${b.firstName ?? ''} ${b.lastName ?? ''}`,
                    undefined,
                    { sensitivity: 'base' },
                ),
            );
        } else {
            arr.sort((a, b) => {
                const ta = a.lastLoginAt
                    ? new Date(a.lastLoginAt).getTime()
                    : 0;
                const tb = b.lastLoginAt
                    ? new Date(b.lastLoginAt).getTime()
                    : 0;
                return tb - ta;
            });
        }
        return arr;
    }, [processedData, mobileSort]);

    const columns: MRT_ColumnDef<any>[] = [
        {
            id: 'fullName',
            header: 'User',
            accessorFn: (row) => `${row.firstName} ${row.lastName}`,
            filterFn: 'fuzzy',
            size: 280,
            mantineTableBodyCellProps: {
                style: { padding: 0 },
            },
            Cell: ({ row, cell }) => {
                const name = cell.getValue<string>()?.trim() ?? '';
                const isSelf =
                    loggedInUser?.sub != null &&
                    String(loggedInUser.sub) === String(row?.original?.id);

                return (
                    <List sx={{ padding: '0' }}>
                        <ListItem
                            sx={{
                                padding: '0',
                                pl: '4px',
                                alignItems: 'flex-start',
                            }}
                        >
                            {isMobile ? null : (
                                <ListItemAvatar
                                    sx={{ padding: '0', minWidth: 52 }}
                                >
                                    {row?.original?.profilePicture?.src ? (
                                        <Box
                                            component='img'
                                            src={
                                                row?.original?.profilePicture
                                                    ?.src
                                            }
                                            alt=''
                                            sx={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                                backgroundColor: 'secondary',
                                            }}
                                        />
                                    ) : (
                                        <ProfilePicture
                                            profileDetails={row?.original}
                                            src={
                                                row?.original?.profilePicture
                                                    ?.src
                                            }
                                            sx={{
                                                width: '40px',
                                                height: '40px',
                                                fontSize: '14px',
                                            }}
                                        />
                                    )}
                                </ListItemAvatar>
                            )}
                            <ListItemText
                                primary={
                                    <Stack
                                        direction='row'
                                        alignItems='center'
                                        gap={0.75}
                                        flexWrap='wrap'
                                    >
                                        <Typography
                                            component='span'
                                            variant='body2'
                                            fontWeight={700}
                                        >
                                            {name}
                                        </Typography>
                                        {isSelf ? (
                                            <Box
                                                component='span'
                                                sx={loggedInUserBadge()}
                                            >
                                                You
                                            </Box>
                                        ) : null}
                                    </Stack>
                                }
                                secondary={
                                    <Typography
                                        component='span'
                                        variant='body2'
                                        color='text.secondary'
                                    >
                                        {row?.original?.email}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                );
            },
        },
        {
            id: 'roleAccess',
            header: 'Role & access',
            size: 200,
            accessorFn: (row) => roleAccessLabelExport(row, loggedInUser),
            mantineTableBodyCellProps: { style: { padding: 0 } },
            Cell: ({ row }) => {
                const r = row?.original;
                const adminish =
                    r?.role?.includes?.('admin') ||
                    loggedInUser?.role === 'admin';
                const label = roleAccessLabel(r, loggedInUser);
                const accessLine =
                    r?.viewOnly === true ? 'View access' : 'Full access';

                return (
                    <Stack
                        spacing={0.5}
                        alignItems='flex-start'
                        sx={{ py: 0.5 }}
                    >
                        <Box component='span' sx={roleAccessBadgeSx(adminish)}>
                            {label}
                        </Box>
                        <Typography variant='body2' color='text.secondary'>
                            {accessLine}
                        </Typography>
                    </Stack>
                );
            },
        },
        {
            id: 'department',
            header: loggedInUser?.role.includes('guest') ? 'Team' : '',
            mantineTableBodyCellProps: { style: { padding: 0 } },
            accessorFn: (row) => capitalizeName(row?.department?.name ?? ''),
            Cell: ({ row }) => {
                const department = row?.original?.department?.name;

                return loggedInUser?.role.includes('guest') ? (
                    department ? (
                        <Typography variant='body2'>
                            {capitalizeName(department)}
                        </Typography>
                    ) : (
                        <Typography variant='body2'>
                            <CustomTooltip title='Missing Team: Team Managers will not see, manage or view their staff /reservations unless they have been added to the same team as the manager'>
                                <ErrorIcon color='warning' />
                            </CustomTooltip>
                        </Typography>
                    )
                ) : null;
            },
        },
        {
            accessorKey: 'lastLoginAt',
            header: 'Last active',
            filterVariant: 'date',
            filterFn: 'greaterThanOrEqualTo',
            mantineFilterDateInputProps: {
                valueFormat: 'DD/MM/YYYY',
            },
            Cell: ({ cell }) => {
                const value = cell.getValue() as Date | null;
                return (
                    <List sx={{ padding: '0!important' }}>
                        <ListItem sx={{ padding: '0!important' }}>
                            <ListItemText
                                primary={
                                    <Typography variant='body2'>
                                        {value ? moment(value).fromNow() : '—'}
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        variant='body2'
                                        color='text.secondary'
                                    >
                                        {value
                                            ? moment(value).format(
                                                  'DD/MM/YYYY, h:mm a',
                                              )
                                            : ''}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                );
            },
        },
        {
            accessorKey: 'isActive',
            header: 'Status',
            filterVariant: 'select',
            filterFn: (row, _columnId, filterValue) => {
                if (!filterValue) return true;
                return String(row.getValue('isActive')) === filterValue;
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Enabled', value: 'true' },
                    { label: 'Disabled', value: 'false' },
                ],
                placeholder: 'Filter by Status',
                clearable: true,
            },
            Cell: ({ cell }) => {
                const value = cell.getValue();

                return isIpad || isMobile ? (
                    value === true ? (
                        <CustomTooltip title='Enabled'>
                            <DoneIcon sx={{ color: 'primary' }} />
                        </CustomTooltip>
                    ) : (
                        <CustomTooltip title='Disabled'>
                            <PersonOffIcon />
                        </CustomTooltip>
                    )
                ) : (
                    <CustomChip
                        style={{ cursor: 'pointer' }}
                        sx={{ typography: 'body2' }}
                        label={value === true ? 'Enabled' : 'Disabled'}
                        color={value === true ? 'green' : 'red'}
                        variant='outlined'
                    />
                );
            },
        },
        {
            id: 'rowActions',
            header: '',
            size: 52,
            enableColumnFilter: false,
            mantineTableBodyCellProps: {
                style: { padding: 0, textAlign: 'right' as const },
            },
            Cell: ({ row }) => (
                <Box
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                    <UserRowActionsMenu row={row.original} />
                </Box>
            ),
        },
    ];

    const linkState = { name: 'viewUser' };

    const genericTable = (
        <GenericTable
            columns={columns}
            data={processedData}
            tableName='Users'
            loading={skipList ? false : isUserListLoading}
            pagination={{ pageSize: 10, pageIndex: 0 }}
            enableGlobalFilter={false}
            onRowClick={(row) => {
                navigate(userProfilePath(row), { state: linkState });
            }}
            columnVisibility={{
                department: loggedInUser?.role.includes('guest'),
            }}
            linkType={LinkType.User}
            linkState={linkState}
            paginationItemLabel='users'
            hideTopToolbar
            cellStyles={{
                paddingBottom: 0,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: theme.typography.body2.fontWeight,
                lineHeight: theme.typography.body2.lineHeight,
                letterSpacing: theme.typography.body2.letterSpacing,
            }}
        />
    );

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            {isMobile ? (
                <>
                    <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                        spacing={1}
                        sx={{ mb: 2 }}
                    >
                        <Typography variant='subtitle1' fontWeight={600}>
                            All users ({sortedMobileRows.length})
                        </Typography>
                        <FormControl size='small' sx={{ minWidth: 168 }}>
                            <Select
                                value={mobileSort}
                                onChange={(e: SelectChangeEvent) =>
                                    setMobileSort(
                                        e.target.value as 'lastActive' | 'name',
                                    )
                                }
                                renderValue={(v) =>
                                    v === 'lastActive'
                                        ? 'Sort: Last active'
                                        : 'Sort: Name (A–Z)'
                                }
                            >
                                <MenuItem value='lastActive'>
                                    Last active
                                </MenuItem>
                                <MenuItem value='name'>Name (A–Z)</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack spacing={0} divider={<Divider />}>
                        {sortedMobileRows.map((row: any) => {
                            const name =
                                `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
                            const isSelf =
                                loggedInUser?.sub != null &&
                                String(loggedInUser.sub) === String(row?.id);
                            const adminish =
                                row?.role?.includes?.('admin') ||
                                loggedInUser?.role === 'admin';
                            const label = roleAccessLabel(row, loggedInUser);
                            const department = row?.department?.name;
                            const last = row.lastLoginAt as Date | null;

                            return (
                                <Box
                                    key={String(row.id ?? row.email ?? name)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        py: 1.75,
                                    }}
                                >
                                    <Box
                                        component={Link}
                                        to={userProfilePath(row)}
                                        state={{
                                            name: 'viewUser',
                                            params: row,
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1.5,
                                            flex: '1 1 auto',
                                            minWidth: 0,
                                            textDecoration: 'none',
                                            color: 'inherit',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                flexShrink: 0,
                                                mt: 0.125,
                                            }}
                                        >
                                            {row?.profilePicture?.src ? (
                                                <Box
                                                    component='img'
                                                    src={row.profilePicture.src}
                                                    alt=''
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        borderRadius: '50%',
                                                        objectFit: 'cover',
                                                        bgcolor:
                                                            'secondary.main',
                                                    }}
                                                />
                                            ) : (
                                                <ProfilePicture
                                                    profileDetails={row}
                                                    src={
                                                        row?.profilePicture?.src
                                                    }
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        fontSize: 14,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Box
                                            sx={{
                                                flex: '1 1 auto',
                                                minWidth: 0,
                                            }}
                                        >
                                            <Stack
                                                direction='row'
                                                alignItems='center'
                                                gap={0.75}
                                                flexWrap='wrap'
                                            >
                                                <Typography
                                                    component='span'
                                                    variant='body1'
                                                    fontWeight={700}
                                                >
                                                    {name}
                                                </Typography>
                                                {isSelf ? (
                                                    <Box
                                                        component='span'
                                                        sx={loggedInUserBadge()}
                                                    >
                                                        You
                                                    </Box>
                                                ) : null}
                                            </Stack>
                                            {loggedInUser?.role.includes(
                                                'guest',
                                            ) ? (
                                                <Typography
                                                    variant='body2'
                                                    color='text.secondary'
                                                    sx={{
                                                        display: 'block',
                                                        mt: 0.75,
                                                    }}
                                                >
                                                    {department ? (
                                                        capitalizeName(
                                                            department,
                                                        )
                                                    ) : (
                                                        <CustomTooltip title='Missing Team: Team Managers will not see, manage or view their staff /reservations unless they have been added to the same team as the manager'>
                                                            <ErrorIcon color='warning' />
                                                        </CustomTooltip>
                                                    )}
                                                </Typography>
                                            ) : null}
                                            <Box
                                                component='span'
                                                sx={{
                                                    ...roleAccessBadgeSx(
                                                        adminish,
                                                    ),
                                                    mt: 0.75,
                                                    display: 'inline-flex',
                                                }}
                                            >
                                                {label}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Stack
                                        spacing={0.75}
                                        alignItems='flex-end'
                                        sx={{
                                            flexShrink: 0,
                                            ml: 'auto',
                                            maxWidth: '42%',
                                        }}
                                    >
                                        <CustomTooltip
                                            title={
                                                row.isActive === true
                                                    ? 'Enabled'
                                                    : 'Disabled'
                                            }
                                        >
                                            <Box
                                                component='span'
                                                role='img'
                                                aria-label={
                                                    row.isActive === true
                                                        ? 'Enabled'
                                                        : 'Disabled'
                                                }
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color:
                                                        row.isActive === true
                                                            ? 'success.main'
                                                            : 'error.main',
                                                }}
                                            >
                                                {row.isActive === true ? (
                                                    <TaskAltIcon fontSize='small' />
                                                ) : (
                                                    <NoAccountsIcon fontSize='small' />
                                                )}
                                            </Box>
                                        </CustomTooltip>
                                        <Typography
                                            variant='body2'
                                            color='text.secondary'
                                        >
                                            {last
                                                ? moment(last).fromNow()
                                                : '—'}
                                        </Typography>
                                        <UserRowActionsMenu row={row} />
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </>
            ) : null}
            <Box
                sx={
                    isMobile
                        ? {
                              position: 'absolute',
                              width: 1,
                              height: 1,
                              p: 0,
                              m: -1,
                              overflow: 'hidden',
                              clip: 'rect(0, 0, 0, 0)',
                              whiteSpace: 'nowrap',
                              border: 0,
                          }
                        : undefined
                }
                aria-hidden={isMobile ? true : undefined}
            >
                {genericTable}
            </Box>
        </Box>
    );
}
